import { requestContext } from 'src/queries/requestContext'
import { RootState } from 'src/redux/createRootReducer'

/**
 * Interop write-through: mirror `state.auth.isCspAuthenticated` into the
 * non-Redux `requestContext` on every change — the config sibling of the
 * token write-through (`primeTokenStore` / `tokenStoreWriteThroughMiddleware`
 * in `tokenStore.interop.ts`). Redux remains the WRITE source; `requestContext`
 * is the transport READ source for the `X-Extend-Platform` header.
 *
 * The flag cannot be derived from `tokenStore.getCspJwt()` presence: on a
 * page load with a CSP cookie the token is present while the flag stays
 * false until `GET_CSP_ME_SUCCESS` verifies the session, and the platform
 * header must stay 'web' through that window.
 *
 * Wired only on the browser entry (`src/client.tsx`), like the token mirror.
 * The server seeds a request-scoped snapshot directly via
 * `runWithRequestContext` and never subscribes a per-request store here.
 */
type SubscribableStore = {
  getState: () => RootState
  subscribe: (listener: () => void) => () => void
}

export const startRequestContextMirror = (
  store: SubscribableStore,
): (() => void) => {
  const sync = (): void => {
    requestContext.setIsCspAuthenticated(
      store.getState().auth.isCspAuthenticated,
    )
  }

  sync()
  return store.subscribe(sync)
}
